import React from 'react';
import { Tooltip, Box, Typography } from '@mui/material';
import {
  CheckCircle, Schedule, ErrorOutline, Cancel, EventAvailable, EventBusy,
  Payments as CashIcon, PhoneAndroid, AccountBalance, CreditCard, HelpOutline,
  TrendingUp, DoneAll, Savings, Bolt,
} from '@mui/icons-material';
import { ACCENTS, tint } from '../theme/accents';

// The single place a state value becomes something a person can see.
//
// The rule: state is always an icon plus a short label, drawn the same way
// everywhere. Before this, one payment status could appear as a bare glyph in
// the Payments grid, a green MUI chip on the Dashboard, an accent chip in the
// member ledger, and the raw word "completed" in the collection dialogs — the
// same fact reading four different ways depending on the screen.
//
// The icon leads, because that is what stopped labels truncating to "Compl…".
// The label always accompanies it, because an icon alone forces a hover to
// answer "did this get paid?". `compact` drops the label for genuinely cramped
// spots — reach for it rarely; needing it usually means the column is too tight.

const STATUS_META = {
  // payments
  completed: { icon: CheckCircle, accent: ACCENTS.emerald, label: 'Paid' },
  pending: { icon: Schedule, accent: ACCENTS.marigold, label: 'Due' },
  overdue: { icon: ErrorOutline, accent: ACCENTS.vermilion, label: 'Overdue' },
  // penalties
  paid: { icon: CheckCircle, accent: ACCENTS.emerald, label: 'Paid' },
  // meetings
  scheduled: { icon: EventAvailable, accent: ACCENTS.teal, label: 'Scheduled' },
  cancelled: { icon: EventBusy, accent: ACCENTS.vermilion, label: 'Cancelled' },
  // loans
  active: { icon: TrendingUp, accent: ACCENTS.indigo, label: 'Active' },
  closed: { icon: DoneAll, accent: ACCENTS.emerald, label: 'Closed' },
  written_off: { icon: Cancel, accent: ACCENTS.vermilion, label: 'Written off' },
  // attendance
  present: { icon: CheckCircle, accent: ACCENTS.emerald, label: 'Present' },
  late: { icon: Schedule, accent: ACCENTS.marigold, label: 'Late' },
  absent: { icon: Cancel, accent: ACCENTS.vermilion, label: 'Absent' },
  // a member who has never been billed for the month in question
  none: { icon: HelpOutline, accent: ACCENTS.silver, label: 'Not billed' },
};

// 'completed' means different things in different tables: a completed payment
// is "Paid", a completed meeting is not. Callers pass a domain to disambiguate;
// without one the payments reading applies, which is the common case.
const DOMAIN_OVERRIDES = {
  meetings: {
    completed: { icon: EventAvailable, accent: ACCENTS.emerald, label: 'Held' },
    pending: { icon: Schedule, accent: ACCENTS.marigold, label: 'Upcoming' },
  },
  loans: {
    completed: { icon: DoneAll, accent: ACCENTS.emerald, label: 'Repaid' },
  },
};

const UNKNOWN = { icon: HelpOutline, accent: ACCENTS.silver, label: 'Unknown', subdued: true };

const statusMeta = (status, domain) =>
  (domain && DOMAIN_OVERRIDES[domain]?.[status]) ||
  STATUS_META[status] ||
  (status ? { ...UNKNOWN, label: String(status) } : UNKNOWN);

// payments.payment_method is free text in the column, so an unrecognised value
// still renders rather than disappearing.
const METHOD_META = {
  Cash: { icon: CashIcon, label: 'Cash', accent: ACCENTS.emerald },
  UPI: { icon: PhoneAndroid, label: 'UPI', accent: ACCENTS.indigo },
  'Bank Transfer': { icon: AccountBalance, label: 'Bank transfer', accent: ACCENTS.teal },
  Card: { icon: CreditCard, label: 'Card', accent: ACCENTS.magenta },
  Other: { icon: HelpOutline, label: 'Other', accent: ACCENTS.silver },
};

// The one list of methods. Any select offering methods must read from this, or
// it drifts — a hand-written copy in the collection dialog was missing "Other".
export const METHOD_VALUES = Object.keys(METHOD_META);

const methodMeta = (method) => {
  if (!method) return { ...UNKNOWN, label: 'Not recorded' };
  return METHOD_META[method] || { ...UNKNOWN, label: String(method) };
};

// expenses.category — the group's spending buckets. Gold and silver get the
// metal hues they carry everywhere else in the app.
const CATEGORY_META = {
  gold: { icon: Savings, label: 'Gold', accent: ACCENTS.gold },
  silver: { icon: Savings, label: 'Silver', accent: ACCENTS.silver },
  meeting: { icon: EventAvailable, label: 'Meeting', accent: ACCENTS.teal },
  utility: { icon: Bolt, label: 'Utility', accent: ACCENTS.indigo },
  other: { icon: HelpOutline, label: 'Other', accent: ACCENTS.magenta },
};

export const CATEGORY_VALUES = Object.keys(CATEGORY_META);

const categoryMeta = (category) =>
  CATEGORY_META[category] || { ...UNKNOWN, label: category ? String(category) : 'Uncategorised' };

const SIZES = {
  small: { dot: 22, icon: 13, gap: 0.625 },
  medium: { dot: 26, icon: 15, gap: 0.75 },
};

// icon + label, drawn identically wherever state appears
const Mark = ({ meta, compact, size = 'medium' }) => {
  const { icon: Icon, accent, label, subdued } = meta;
  const s = SIZES[size] || SIZES.medium;

  const dot = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: s.dot,
        height: s.dot,
        borderRadius: '50%',
        color: subdued ? 'text.secondary' : accent,
        bgcolor: subdued ? 'action.hover' : tint(accent, 0.16),
        border: '1px solid',
        borderColor: subdued ? 'divider' : tint(accent, 0.4),
        '& svg': { fontSize: s.icon },
      }}
    >
      <Icon />
    </Box>
  );

  // Icon-only still needs the label reachable, so it keeps the tooltip.
  if (compact) {
    return (
      <Tooltip title={label} arrow>
        <Box component="span" role="img" aria-label={label} tabIndex={0} sx={{ display: 'inline-flex' }}>
          {dot}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      component="span"
      role="img"
      aria-label={label}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: s.gap, minWidth: 0 }}
    >
      {dot}
      <Typography
        component="span"
        variant="body2"
        noWrap
        sx={{ color: subdued ? 'text.secondary' : 'text.primary', lineHeight: 1.2 }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export const StatusIndicator = ({ status, domain, compact = false, size }) => (
  <Mark meta={statusMeta(status, domain)} compact={compact} size={size} />
);

export const MethodIndicator = ({ method, compact = false, size }) => (
  <Mark meta={methodMeta(method)} compact={compact} size={size} />
);

// Select rows use the very same mark, so the closed field and the table cell
// that later shows the saved value are visually identical.
export const MethodOptionContent = ({ method }) => <MethodIndicator method={method} size="small" />;

export const StatusOptionContent = ({ status, domain }) => (
  <StatusIndicator status={status} domain={domain} size="small" />
);

export const CategoryIndicator = ({ category, compact = false, size }) => (
  <Mark meta={categoryMeta(category)} compact={compact} size={size} />
);

export const CategoryOptionContent = ({ category }) => (
  <CategoryIndicator category={category} size="small" />
);

export const categoryLabel = (category) => categoryMeta(category).label;
export const categoryAccent = (category) => categoryMeta(category).accent;

export const statusLabel = (status, domain) => statusMeta(status, domain).label;
export const statusAccent = (status, domain) => statusMeta(status, domain).accent;
export const methodLabel = (method) => methodMeta(method).label;

export default StatusIndicator;
